import { Form, Input, message, Modal, Select } from "antd";
import { COUNTRIES } from "common/constants";
import { useState } from "react";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import {
  useAddProcessServerMutation,
  useUpdateProcessServerMutation,
} from "features/processServersTable/processServersAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 350px;
`;

const getPostalCodeMaxLength = (country) => {
  if (country === "US") return 5;
  if (country === "CA") return 7;
  return undefined;
};

export default function AddProcessServerModal({ onOk, onCancel, record }) {
  const [form] = Form.useForm();
  const { data: constants } = useFetchBackendConstantsQuery();
  const [addProcessServer] = useAddProcessServerMutation();
  const [updateProcessServer] = useUpdateProcessServerMutation();

  const [country, setCountry] = useState(record?.country ?? "US");

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const onCountryChange = (value) => {
    setCountry(value);
    form.setFieldsValue({ state: undefined, zipCode: undefined });
  };

  const onSubmit = async () => {
    const newProcessServer = await form.validateFields();
    let result;
    if (record) {
      result = await updateProcessServer({
        ...record,
        ...newProcessServer,
        processServerId: record.id,
      });
    } else {
      result = await addProcessServer(newProcessServer);
    }
    if ("data" in result) {
      message.success(`Process server ${record ? "updated" : "added"} successfully!`, 2);
      onOk();
      form.resetFields();
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
    }
  };

  return (
    <Modal
      maskClosable={false}
      title="New Process Server"
      open
      okText="Submit"
      onOk={onSubmit}
      onCancel={onCancel}
    >
      <StyledForm
        form={form}
        layout="vertical"
        initialValues={{ country: "US", ...record }}
        validateMessages={{ required: "This is a required field" }}
      >
        <Form.Item name="code" label="Process Server Code" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="name" label="Process Server Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="address1" label="Address Line 1" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="address2" label="Address Line 2">
          <Input />
        </Form.Item>
        <Form.Item
          name="city"
          label="City"
          rules={[{ required: true }, { pattern: /^[^\d]*$/, message: "Enter a valid city" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="country" label="Country" rules={[{ required: true }]}>
          <Select
            placeholder="Select a country..."
            popupMatchSelectWidth={false}
            showSearch
            filterOption={filterOption}
            options={COUNTRIES}
            onChange={onCountryChange}
          />
        </Form.Item>
        <Form.Item name="state" label="State/Province" rules={[{ required: true }]}>
          {country === "US" || country === "CA" ? (
            <Select
              placeholder="Select a state/province..."
              popupMatchSelectWidth={false}
              showSearch
              filterOption={filterOption}
              options={(country === "CA" ? constants?.canadianProvinces : constants?.states)?.map(
                (state) => ({
                  label: `${state.display} (${state.value})`,
                  value: state.value,
                }),
              )}
            />
          ) : (
            <Input placeholder="Enter State/Province..." />
          )}
        </Form.Item>
        <Form.Item
          name="zipCode"
          label="Zip/Postal Code"
          dependencies={["country"]}
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const selectedCountry = getFieldValue("country");
                if (selectedCountry !== "US") {
                  return Promise.resolve();
                }
                if (!value || /^\d{5}(-\d{4})?$/.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Must be a valid zip code"));
              },
            }),
          ]}
        >
          <Input
            placeholder={country === "CA" ? "A1A 1A1" : "Enter zip code..."}
            maxLength={getPostalCodeMaxLength(country)}
          />
        </Form.Item>
        <Form.Item name="department" label="Department">
          <Input />
        </Form.Item>
        <Form.Item name="ein" label="EIN">
          <Input placeholder="XX-XXXXXXX" />
        </Form.Item>
        <Form.Item name="phone" label="Phone">
          <Input placeholder="XXX-XXX-XXXX" />
        </Form.Item>
        <Form.Item name="fax" label="Fax">
          <Input placeholder="XXX-XXX-XXXX" />
        </Form.Item>
        <Form.Item name="contactName" label="Contact Name">
          <Input />
        </Form.Item>
        <Form.Item name="email" label="Email">
          <Input />
        </Form.Item>
        <Form.Item name="notes" label="Notes / Description">
          <Input />
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}
